import { ErrorMessage, Field, type FieldProps, Form, Formik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import KC_Heading1 from "../../../designsystem/typography/KC-Heading/KC_Heading1";
import KC_Text from "../../../designsystem/typography/KC_Text/KC_Text";
import { KC_Text_Sizes, KC_Weights } from "../../../designsystem/ds_commons";
import { useHistory } from "react-router-dom";
import LoginV2API from "../api/api";
import { useDispatch } from "react-redux";
import { setMasterToken } from "../../../redux/actions/user";
import * as Yup from "yup";
import { storeMasterToken } from "../../../utils/helpers/localStorage";
import { EMAIL_VERIFICATION } from "../../../utils/routes/routes";
import CodeValidationFooter from "./module/CodeValidationFooter";
import fetchAllManagedAccounts from "../../../api/fetchAllManagedAccounts";
import KC_Heading2 from "../../../designsystem/typography/KC-Heading/KC_Heading2";
import { FullScreenLoader } from "../../../components/Loaders/FullScreenLoader/FullScreenLoader";
import {
  fetchEventsAndPOSAData,
  setError,
  useSubscriptionPlansSelector
} from "../../../redux/actions/app";
import { tt } from "../../../utils/i18n/i18n";
import BaseJSBackendResponse from "../../../model/responsemodel/BaseJSBackendResponse";
import ReturnCodes from "../../../utils/error_handling/ReturnCodes";
import getErrorMessage from "../../../utils/error_handling/getErrorMessage";
import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import {
  BASE_URL,
  fetchNeutreEventAndUpdateDefaults,
  STORAGE_SIGNUP_FORM_VALUES_KEY
} from "../../../utils/constants";
import { useUrlQuery } from "../../../utils/hooks/useUrlQuery";
import CONFIG from "../../../model/TSConfig";

const ValidationSchema = Yup.object().shape({
  code: Yup.string()
    .matches(/^\d{6}$/, tt("code_validation_error_exact_digits"))
    .required(tt("code_validation_error_required"))
});

export default function CodeValidation() {
  const dispatch = useDispatch();
  const history = useHistory();
  const query = useUrlQuery();

  const email = query.get("email");

  const inputRefs = useRef([]);
  const allSubscriptionPlans = useSubscriptionPlansSelector();

  const [isLoading, setIsLoading] = useState(false);
  const [returnCode, setReturnCode] = useState(ReturnCodes.Ok);

  const isSignIn = useMemo(() => {
    return !!query.get("login");
  }, [query]);

  const planId = useMemo(() => {
    const queryPlanId = query.get("planId");

    if (!queryPlanId && allSubscriptionPlans?.length) {
      const defaultPlan = allSubscriptionPlans.find((p) => p.isDefault);
      return defaultPlan?._id || null;
    }

    return queryPlanId;
  }, [query, allSubscriptionPlans]);

  const selectedPlan = useMemo(() => {
    if (!planId || !allSubscriptionPlans) {
      return null;
    }
    return allSubscriptionPlans.find((plan) => plan._id === planId);
  }, [planId, allSubscriptionPlans]);

  const errorData = useMemo(() => {
    return {
      hasError: returnCode !== ReturnCodes.Ok,
      msg: getErrorMessage(returnCode)
    };
  }, [returnCode]);

  const onResendMailWithValidationCode = async () => {
    if (!email) {
      dispatch(setError(tt("code_validation_error_no_email")));
      setTimeout(() => {
        history.push(EMAIL_VERIFICATION);
      }, 3000);
      return;
    }
    try {
      await LoginV2API.resendCode(email);
    } catch (e) {
      dispatch(setError(tt("code_validation_error_no_email")));
      setTimeout(() => {
        history.push(EMAIL_VERIFICATION);
      }, 3000);
    }
  };

  const handleSubmit = async (values: any) => {
    setIsLoading(true);

    try {
      const res = await LoginV2API.validateCode(
        values.code,
        email || "",
        planId
      );

      // Clear form values
      if (CONFIG.env !== "dev") {
        localStorage.removeItem(STORAGE_SIGNUP_FORM_VALUES_KEY);
      }

      if (res.redirectLink) {
        window.location.href = res.redirectLink;
        return;
      }

      dispatch(setMasterToken(res.token));
      storeMasterToken(res.token);

      if (selectedPlan && selectedPlan.price === 0) {
        dispatch(
          fetchAllManagedAccounts({
            isSignIn,
            history,
            mustAssignFreePlan: true,
            plan: selectedPlan
          })
        );
      } else {
        dispatch(fetchAllManagedAccounts({ isSignIn: true, history }));
      }

      await Promise.all([
        fetchNeutreEventAndUpdateDefaults(),
        fetchEventsAndPOSAData()(dispatch)
      ]);
    } catch (e) {
      //@ts-ignore
      const payload = e?.response?.data as BaseJSBackendResponse<null>;

      if (payload) {
        setReturnCode(payload.code);
      } else {
        setReturnCode(ReturnCodes.Generic);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => void,
    values: { code: string },
    formikHandleSubmit: (
      e?: React.FormEvent<HTMLFormElement> | undefined
    ) => void
  ) => {
    const { value } = e.target;
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = values.code.split("");
      newCode[index] = value;
      const newCodeString = newCode.join("");

      // @ts-ignore
      setFieldValue("code", newCodeString, false).then(() => {
        if (value.length === 1) {
          if (index < 5) {
            // @ts-ignore
            inputRefs.current[index + 1]?.focus();
          }
          // If all digits are filled, submit the form
          if (newCodeString.length === 6 && /^\d{6}$/.test(newCodeString)) {
            formikHandleSubmit();
          }
        }
      });
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (
      e.key === "Backspace" &&
      !(e.target as HTMLInputElement).value &&
      index > 0
    ) {
      // @ts-expect-error
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => void,
    formikHandleSubmit: (
      e?: React.FormEvent<HTMLFormElement> | undefined
    ) => void
  ) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (/^\d{1,6}$/.test(pastedData)) {
      // @ts-ignore
      setFieldValue("code", pastedData.padEnd(6, "")).then(() => {
        // @ts-ignore
        inputRefs.current[Math.min(pastedData.length, 5)]?.focus();
        if (pastedData.length === 6) {
          formikHandleSubmit();
        }
      });
    }
  };

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
    document.getElementById("sign-up-validation-0")?.focus();
  }, []);

  if (isLoading) {
    return <FullScreenLoader loading={isLoading} />;
  }

  return (
    <>
      <img
        src={`${BASE_URL}/background/bg_01_1.png`}
        className="auth-bg"
        alt="background_img"
      />

      <div id="code-validation-wrapper" className="signup-container">
        <div className="auth-logo-container">
          <img
            alt="logo-kadow"
            src={`${BASE_URL}/img/logo/kadow_1_line.png`}
            className="logo-kadow"
          />
        </div>

        <div className="mx-auto d-flex flex-column signup-content">
          <div className="d-flex flex-column justify-content-center align-items-center w-100 st-gap">
            <KC_Heading1
              text={tt("code_validation_heading")}
              className="display-lg text-center"
            />
            <KC_Heading2
              text={tt("code_validation_heading")}
              className="display-sm text-center"
            />
            <KC_Text
              size={KC_Text_Sizes.lg}
              weight={KC_Weights.reg}
              className="text-center"
              text={tt("code_validation_subheading", { email })}
            />
          </div>

          <Formik
            initialValues={{ code: "" }}
            validationSchema={ValidationSchema}
            onSubmit={handleSubmit}
            validateOnBlur={false}
          >
            {({ values, setFieldValue, errors, touched, handleSubmit }) => {
              return (
                <Form className={"signup-form"}>
                  <div className="d-flex st-gap ">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div
                        key={index}
                        id={`digit-${index}`}
                        className={"kc-input-wrapper"}
                      >
                        <Field name="code">
                          {({ field }: FieldProps) => (
                            <input
                              id={`sign-up-validation-${index}`}
                              {...field}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={values.code[index] || ""}
                              onChange={(e) => {
                                handleChange(
                                  e,
                                  index,
                                  setFieldValue,
                                  values,
                                  handleSubmit
                                );
                              }}
                              onKeyDown={(e) => {
                                handleKeyDown(index, e);
                              }}
                              onPaste={(e) => {
                                handlePaste(e, setFieldValue, handleSubmit);
                              }}
                              // @ts-expect-error
                              ref={(el) => (inputRefs.current[index] = el)}
                              className="kc_input text-center"
                            />
                          )}
                        </Field>
                      </div>
                    ))}
                  </div>

                  {errors.code && touched.code && (
                    <ErrorMessage
                      name={"code"}
                      component="div"
                      className="error-message"
                    />
                  )}

                  {errorData.hasError && (
                    <KC_UIText
                      weight={"reg"}
                      text={errorData.msg}
                      className="text-danger small mt-1"
                    />
                  )}
                  <CodeValidationFooter
                    onResendMail={onResendMailWithValidationCode}
                  />
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </>
  );
}
